'use client';

import type { MouseEvent } from 'react';
import { useSearchParams } from 'next/navigation';
import { useTranslation } from 'react-i18next';
import { Icon } from '@galapagos/design-system/web';
import { sizing } from '@galapagos/design-system/sizing';
import { useSignupNavigation } from '../hooks/useSignupNavigation';
import { SIGNUP_BOOK_A_CALL_PAGE, signupPagePath } from '../off-path-pages';
import { copyrightYear } from '../services/copyright-year';
import { SIGNUP_LEGAL_DOCUMENT_PATHS } from '../legal/legal-document-paths';

/**
 * The signup site footer (Zach's Oct 31 design): a help group with Book a
 * call and Email us, the legal links that open the signup app's own legal
 * pages in a new tab, and the copyright. Stacked on phones, one row on
 * desktop (the stylesheet lays it out).
 * Reuses the app-wide footer's action ids, so a click reads the same in
 * analytics whichever chrome the visitor is on. Book a call shows on every
 * page, as production's footer does.
 */
export function SignupFooter() {
  const { t } = useTranslation('common');
  const { t: tSignup } = useTranslation('signup');
  const email = t('siteFooter.emailAddress');
  const year = copyrightYear();
  const { goToBookACall } = useSignupNavigation();
  const query = useSearchParams().toString();
  const bookCallPath = signupPagePath(SIGNUP_BOOK_A_CALL_PAGE);
  const onBookCall = (event: MouseEvent<HTMLAnchorElement>) => {
    // A modified or non-primary click keeps the browser's own behaviour (a
    // new tab or window), which is why the href stays real.
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    // A plain click replaces, never pushes, like every move in the flow.
    event.preventDefault();
    goToBookACall();
  };
  const newTab = <span className="signup-visually-hidden"> {tSignup('footer.opensInNewTab')}</span>;

  return (
    <footer className="signup-footer" data-testid="signup-footer">
      <div className="signup-footer__help">
        <span className="signup-footer__help-title">{tSignup('footer.needHelp')}</span>
        <a
          className="signup-footer__link signup-footer__link--quiet"
          href={query ? `${bookCallPath}?${query}` : bookCallPath}
          onClick={onBookCall}
          data-analytics-action="signup.footer-book-call.activate"
          data-testid="signup-footer-book-call"
        >
          <Icon name="calendar" size={sizing.icon.xs} aria-hidden />
          {tSignup('footer.bookCall')}
        </a>
        <a
          className="signup-footer__link signup-footer__link--quiet"
          href={`mailto:${email}`}
          data-analytics-action="site.footer-email.activate"
          data-testid="signup-footer-email"
        >
          <Icon name="mail" size={sizing.icon.xs} aria-hidden />
          {tSignup('footer.emailUs')}
        </a>
      </div>
      <div className="signup-footer__bottom">
        <nav aria-label={t('siteFooter.legalLabel')}>
          <ul className="signup-footer__legal">
            <li>
              <a
                className="signup-footer__link"
                href={SIGNUP_LEGAL_DOCUMENT_PATHS.termsOfUse}
                target="_blank"
                rel="noopener noreferrer"
                data-analytics-action="site.footer-terms.activate"
                data-testid="signup-footer-legal-link"
              >
                {t('siteFooter.termsOfUse')}
                {newTab}
              </a>
            </li>
            <li>
              <a
                className="signup-footer__link"
                href={SIGNUP_LEGAL_DOCUMENT_PATHS.privacyPolicy}
                target="_blank"
                rel="noopener noreferrer"
                data-analytics-action="site.footer-privacy.activate"
                data-testid="signup-footer-legal-link"
              >
                {t('siteFooter.privacyPolicy')}
                {newTab}
              </a>
            </li>
            <li>
              <a
                className="signup-footer__link"
                href={SIGNUP_LEGAL_DOCUMENT_PATHS.consumerHealthDataPrivacyNotice}
                target="_blank"
                rel="noopener noreferrer"
                data-analytics-action="site.footer-chd.activate"
                data-testid="signup-footer-legal-link"
              >
                {t('siteFooter.consumerHealthDataPrivacyNotice')}
                {newTab}
              </a>
            </li>
            <li>
              <a
                className="signup-footer__link"
                href={SIGNUP_LEGAL_DOCUMENT_PATHS.noticeOfPrivacyPractices}
                target="_blank"
                rel="noopener noreferrer"
                data-analytics-action="site.footer-npp.activate"
                data-testid="signup-footer-legal-link"
              >
                {t('siteFooter.noticeOfPrivacyPractices')}
                {newTab}
              </a>
            </li>
            <li>
              <a
                className="signup-footer__link"
                href={SIGNUP_LEGAL_DOCUMENT_PATHS.aiNotice}
                target="_blank"
                rel="noopener noreferrer"
                data-analytics-action="site.footer-ai-notice.activate"
                data-testid="signup-footer-legal-link"
              >
                {t('siteFooter.aiNotice')}
                {newTab}
              </a>
            </li>
            <li>
              <a
                className="signup-footer__link"
                href={SIGNUP_LEGAL_DOCUMENT_PATHS.tcpaAuthorization}
                target="_blank"
                rel="noopener noreferrer"
                data-analytics-action="site.footer-tcpa.activate"
                data-testid="signup-footer-legal-link"
              >
                {t('siteFooter.tcpaAuthorization')}
                {newTab}
              </a>
            </li>
          </ul>
        </nav>
        <p className="signup-footer__copyright" data-testid="signup-footer-copyright">
          {t('siteFooter.copyright', { year })}
        </p>
      </div>
    </footer>
  );
}
